import { createFileRoute, useSearch } from "@tanstack/react-router"
import { LogoFull } from "@/components/LogoFull"
import { ErrorCard } from "@/components/ErrorCard.tsx"
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field.tsx"
import { Input } from "@/components/ui/input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { CheckCircle } from "lucide-react"
import { usePageTitle } from "@/contexts/PageTitleContext.tsx"
import { useEffect, useState } from "react"
import { useMutation } from "@tanstack/react-query"
import { authApi } from "@/api/auth.api.ts"
import { useTranslation } from "react-i18next"
import { cn, getStoredLocale } from "@/lib/utils.ts"

export const Route = createFileRoute("/_unauthorized/account/reset-password")({
  component: RouteComponent,
})

function RouteComponent() {
  const { setTitle } = usePageTitle()
  const { c }: { c: string } = useSearch({
    from: "/_unauthorized/account/reset-password",
  })
  const { t } = useTranslation()
  const locale = getStoredLocale()

  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [password, setPassword] = useState<string>("")
  const [repeatPassword, setRepeatPassword] = useState<string>("")
  const [passwordError, setPasswordError] = useState({
    minLength: true,
    hasUppercase: true,
    hasNumber: true,
    hasSpecialChar: true,
  })
  const [repeatError, setRepeatError] = useState(false)

  const onChangePassword = (e) => {
    setPassword(e.target.value)
    validatePassword()
  }
  const onChangeRepeatPassword = (e) => {
    setRepeatError(false)
    setRepeatPassword(e.target.value)
  }

  const validatePassword = () => {
    setPasswordError({
      minLength: password.length >= 6,
      hasUppercase: /[A-Z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /['"!@#$*;&:?\-+]/.test(password),
    })
  }

  const validateRepeatPassword = () => {
    if (password !== repeatPassword) {
      setRepeatError(true)
    }
  }

  const resetPassword = useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      setIsSubmitted(true)
      setIsSubmitting(false)
    },
  })
  const onSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    resetPassword.mutate({ c, password })
  }

  if (!c) {
    if (!c) {
      return (
        <div className="flex h-full w-full max-w-md flex-col items-center p-4 text-center">
          <div className="mt-4 mb-4">
            <img src={LogoFull} alt="Egab" />
          </div>
          <ErrorCard />
        </div>
      )
    }
  }

  useEffect(() => {
    setTitle(t("reset.pageTitle"))
  }, [])

  return (
    <div className="flex h-full w-full max-w-md flex-col items-center p-4 text-center">
      <LogoFull />
      {!isSubmitted ? (
        <>
          <h2
            className={cn(
              "mb-4 text-center font-heading text-xl",
              locale === "ar" && "font-arabic!"
            )}
          >
            {t("reset.title")}
          </h2>
          <div className="flex w-full flex-col gap-6">
            <form className="flex w-full flex-col gap-4">
              <FieldGroup className="w-full min-w-sm">
                <Field>
                  <div className="flex items-center">
                    <FieldLabel htmlFor="password">
                      {t("signup.password")}
                    </FieldLabel>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Password"
                    required
                    className="h-10"
                    value={password}
                    onChange={onChangePassword}
                    onBlur={validatePassword}
                  />
                  <>
                    {!passwordError.minLength && (
                      <FieldError className="text-start">
                        {t("signup.minLength")}
                      </FieldError>
                    )}
                    {!passwordError.hasUppercase && (
                      <FieldError className="text-start">
                        {t("signup.upperCase")}
                      </FieldError>
                    )}
                    {!passwordError.hasNumber && (
                      <FieldError className="text-start">
                        {t("signup.number")}
                      </FieldError>
                    )}
                    {!passwordError.hasSpecialChar && (
                      <FieldError className="text-start">
                        {t("signup.special")}
                      </FieldError>
                    )}
                  </>
                </Field>
                <Field>
                  <div className="flex items-center">
                    <FieldLabel htmlFor="passwordRepeat">
                      {t("signup.repeatPassword")}
                    </FieldLabel>
                  </div>
                  <Input
                    id="passwordRepeat"
                    type="password"
                    placeholder="Repeat Password"
                    required
                    className="h-10"
                    value={repeatPassword}
                    onChange={onChangeRepeatPassword}
                    onBlur={validateRepeatPassword}
                  />
                  {repeatError && (
                    <FieldError className="text-start">
                      {t("signup.repeatPasswordError")}
                    </FieldError>
                  )}
                </Field>
              </FieldGroup>
              <FieldGroup>
                <Button
                  type="submit"
                  disabled={
                    isSubmitting ||
                    Object.values(passwordError).includes(false) ||
                    repeatError
                  }
                  size="lg"
                  onClick={onSubmit}
                >
                  {isSubmitting ? t("reset.submitting") : t("reset.sumbit")}
                </Button>
              </FieldGroup>
            </form>
          </div>
        </>
      ) : (
        <div className="m-8 flex flex-col items-center gap-4 text-center font-heading text-lg">
          <CheckCircle size="48" />
          {t("reset.successTitle")}
          <span className="font-sans text-sm">{t("reset.subtitle")}</span>
          <div
            className="m-8 font-sans"
            onClick={() => {
              window.location.replace("/login")
            }}
          >
            <Button type="button" size="lg">
              {t("common.LoginNow")}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
